import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import { useBanners, useMemberCount, useUnitCount } from "@/hooks/useSiteQueries";
import { withMemberCountInText } from "@/lib/memberCountDisplay";
import { withUnitCountInText } from "@/lib/unitCountDisplay";

const AUTO_INTERVAL_MS = 4000;

function applyDynamicCounts(text, memberCount, unitCount) {
  return withUnitCountInText(withMemberCountInText(text, memberCount), unitCount);
}

function BannerSlide({ banner, variant, memberCount, unitCount }) {
  const heightClass = variant === "strip" ? "h-36 sm:h-44" : "h-56 sm:h-72";

  const inner = (
    <div className={`relative overflow-hidden rounded-xl border border-gold-light/50 group ${heightClass}`}>
      <img
        src={banner.image_url}
        alt={banner.title}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-maroon/85 via-maroon/60 to-transparent" />
      <div className="relative h-full flex flex-col justify-center px-6 sm:px-10 max-w-xl">
        <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-gold-light mb-1.5">
          Featured
        </p>
        <p className="font-serif text-xl sm:text-2xl font-semibold text-white leading-tight">
          {banner.title}
        </p>
        {banner.subtitle && (
          <p className="mt-1.5 text-xs sm:text-sm text-white/80 line-clamp-2">
            {applyDynamicCounts(banner.subtitle, memberCount, unitCount)}
          </p>
        )}
        <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-gold-light">
          Learn more <ArrowUpRight size={14} />
        </span>
      </div>
    </div>
  );

  if (banner.link_url?.startsWith("/")) {
    return <Link to={banner.link_url}>{inner}</Link>;
  }
  return (
    <a href={banner.link_url || "#"} target="_blank" rel="noreferrer">
      {inner}
    </a>
  );
}

/**
 * Renders admin-managed banner/ad zones for a given placement.
 * Multiple banners auto-rotate every 4s; arrows appear when there is more than one.
 * Placements: home_hero, home_mid, sponsors_page, forum_page
 */
export default function AdBanner({ placement, variant = "strip" }) {
  const { data: banners = [] } = useBanners(placement);
  const { data: memberCount } = useMemberCount();
  const { data: unitCount } = useUnitCount();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const count = banners.length;
  const hasMultiple = count > 1;

  const goTo = useCallback(
    (next) => {
      if (!hasMultiple) return;
      setIndex((next + count) % count);
    },
    [count, hasMultiple]
  );

  const goNext = useCallback(() => goTo(index + 1), [goTo, index]);
  const goPrev = useCallback(() => goTo(index - 1), [goTo, index]);

  useEffect(() => {
    setIndex(0);
  }, [placement, count]);

  useEffect(() => {
    if (!hasMultiple || paused) return undefined;
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % count);
    }, AUTO_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [hasMultiple, paused, count]);

  if (!count) return null;

  return (
    <div
      data-testid={`ad-banner-${placement}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div className="overflow-hidden rounded-xl">
        <div
          className="flex transition-transform duration-500 ease-in-out"
          style={{ transform: `translateX(-${index * 100}%)` }}
          aria-live="polite"
        >
          {banners.map((banner) => (
            <div key={banner.id} className="w-full shrink-0">
              <BannerSlide banner={banner} variant={variant} memberCount={memberCount} unitCount={unitCount} />
            </div>
          ))}
        </div>
      </div>

      {hasMultiple && (
        <div className="mt-3 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={goPrev}
            className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs sm:text-sm font-medium text-maroon shadow-sm hover:border-maroon hover:bg-cream transition-colors shrink-0"
            aria-label="Previous banner"
            data-testid={`ad-banner-${placement}-prev`}
          >
            <ChevronLeft size={16} />
            <span className="hidden sm:inline">Previous</span>
          </button>

          <div
            className="flex items-center justify-center gap-1.5 flex-1"
            data-testid={`ad-banner-${placement}-dots`}
          >
            {banners.map((banner, i) => (
              <button
                key={banner.id}
                type="button"
                onClick={() => setIndex(i)}
                className={`h-2 rounded-full transition-all ${
                  i === index ? "w-5 bg-maroon" : "w-2 bg-gray-300 hover:bg-maroon/40"
                }`}
                aria-label={`Go to banner ${i + 1}`}
                aria-current={i === index ? "true" : undefined}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={goNext}
            className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs sm:text-sm font-medium text-maroon shadow-sm hover:border-maroon hover:bg-cream transition-colors shrink-0"
            aria-label="Next banner"
            data-testid={`ad-banner-${placement}-next`}
          >
            <span className="hidden sm:inline">Next</span>
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
